import { ReactNode, useMemo } from 'react'
import {
  MlCtaBar,
  MlCtaBarProps,
  MlNotification,
  MlNotificationProps,
  MlSiteBanner,
  MlSiteBannerProps,
  MlSnackbar,
  MlSnackbarProps,
} from '../../molecules'
import { FormFieldState } from '../or-booking-panel'
import { OrCardCarousel, OrCardCarouselProps } from '../or-card-carousel'
import { OrTextBlock, OrTextBlockProps } from '../or-text-block'
import { OrMapProps } from '../or-map'
import { cn } from '../../utils'
import { OrBaseContent, OrBaseContentProps } from '../or-base-content'
import dynamic from 'next/dynamic'
import { OrContainerBlock, FormWithTitleAndTypeProps, CustomBlock } from '../or-container'
import { FormRenderer } from '../or-container/form-renderer'

export interface OrContainerTwoColProps {
  columnRatio: string
  blocks: OrContainerBlock[]
  isFormLoading?: boolean
  onSubmitForm?: (
    valid: boolean,
    errors: string[],
    state: FormFieldState,
    submissionType: string,
    gateMetadata?: { hasGatedContent: boolean; gatedContentFilename: string }
  ) => Promise<void>
  formErrorNotificationProps?: MlNotificationProps | null
  formSuccessSnackbarProps?: MlSnackbarProps | null
  gaCookie?: string
  gaFormField?: string
  errorElementId?: string
  gateUnlocked?: boolean
  onGateUnlock?: (formId: string) => void
  successfulFormId?: string
}

const columnRatioClasses: Record<string, { left: string; right: string }> = {
  '50-50': { left: 'slg:w-1/2', right: 'slg:w-1/2' },
  '60-40': { left: 'slg:w-3/5', right: 'slg:w-2/5' },
  '40-60': { left: 'slg:w-2/5', right: 'slg:w-3/5' },
  '70-30': { left: 'slg:w-[70%]', right: 'slg:w-[30%]' },
  '30-70': { left: 'slg:w-[30%]', right: 'slg:w-[70%]' },
}

const DynamicMapLoader = dynamic(() => import('../../organisms').then((mod) => mod.OrMap), {
  ssr: false,
  loading: () => (
    <div className="h-[28.125rem] sm:h-[32.5rem] slg:h-[22rem] lg:h-[23rem] bg-gray-200 animate-pulse flex items-center justify-center">
      <div className="w-[50px] h-[50px] animate-spin rounded-full border-2 border-current border-t-transparent" />
    </div>
  ),
})

const renderBlock = (
  block: OrContainerBlock,
  index: number,
  {
    onSubmitForm,
    isFormLoading,
    formErrorNotificationProps,
    formSuccessSnackbarProps,
    gaFormField,
    gaCookie,
    errorElementId,
    gateUnlocked,
    onGateUnlock,
    successfulFormId,
  }: Omit<OrContainerTwoColProps, 'blocks' | 'columnRatio'>
): ReactNode => {
  switch (block?.type) {
    case 'ml-site-banner': {
      const fields = block.fields as MlSiteBannerProps
      return (
        <section className="!pt-0">
          <MlSiteBanner
            variant={fields?.variant}
            media={fields?.media}
            logo={fields?.logo}
            headline={fields?.headline}
            description={fields?.description}
            cta={fields?.cta}
            orientation={fields?.orientation}
            alignment={fields?.alignment}
            locationOptions={fields?.locationOptions}
            solutions={fields?.solutions}
            defaultSolutionKey={fields?.defaultSolutionKey}
          />
        </section>
      )
    }

    case 'or-text-block': {
      const fields = block.fields as OrTextBlockProps['content']
      return <OrTextBlock key={index} content={fields} className="lg:!pb-6 !px-0" />
    }

    case 'ml-cta-bar': {
      const fields = block.fields as MlCtaBarProps
      return (
        <section>
          <MlCtaBar
            key={index}
            className="max-w-[21.438rem] mx-auto"
            primaryLabel={fields?.primaryLabel}
            primaryIcon={fields?.primaryIcon}
            primaryActionValue={fields?.primaryActionValue}
            primaryTarget={fields?.primaryTarget}
            secondaryLabel={fields?.secondaryLabel}
            secondaryIcon={fields?.secondaryIcon}
            secondaryActionValue={fields?.secondaryActionValue}
            secondaryTarget={fields?.secondaryTarget}
            parentName="OrContainerTwoCol"
          />
        </section>
      )
    }

    case 'form': {
      const fields = block.fields as FormWithTitleAndTypeProps
      const isGateForm = fields.isGate && fields.formId
      const isGateUnlockedForThisForm = !!(isGateForm && gateUnlocked && successfulFormId === fields.formId)

      const errorNotificationProps: MlNotificationProps | null = formErrorNotificationProps
        ? {
            ...formErrorNotificationProps,
            ...(fields.serverErrorNotification ?? {}),
          }
        : null
      const formError = errorNotificationProps ? (
        <MlNotification
          {...errorNotificationProps}
          id={errorElementId || 'or-container-two-col-form-error-notification'}
          className="my-10"
        />
      ) : null

      const formSuccess = formSuccessSnackbarProps ? (
        <>
          <div className="my-8" />
          <MlSnackbar
            {...formSuccessSnackbarProps}
            {...(fields.successSnackbar ?? {})}
            id="form_success"
            className="z-10"
          />
        </>
      ) : null

      return (
        <section id={fields.formId}>
          {formError}
          {formSuccess}
          <FormRenderer
            fields={fields.fields ?? []}
            title={fields.title}
            onSubmit={
              onSubmitForm
                ? (valid, errors, state) =>
                    onSubmitForm(
                      valid,
                      errors,
                      state,
                      fields.submissionType ?? 'General inquiry',
                      fields.isGate && fields.gateAttachedFileUrl && fields.sendGatedContentByEmail
                        ? {
                            hasGatedContent: true,
                            gatedContentFilename: fields.gateAttachedFileUrl.split('/').pop() ?? '',
                          }
                        : undefined
                    )
                : undefined
            }
            onGateUnlock={
              isGateForm && onGateUnlock && fields.formId ? () => onGateUnlock(fields.formId as string) : undefined
            }
            isLoading={isFormLoading ?? false}
            gaFormField={gaFormField ?? ''}
            gaCookie={gaCookie ?? ''}
            isGateUnlocked={isGateUnlockedForThisForm}
            gateAttachedFileUrl={fields.gateAttachedFileUrl}
            sendGatedContentByEmail={fields.sendGatedContentByEmail}
            fieldError={fields.fieldError}
            multipleFieldError={fields.multipleFieldError}
          />
        </section>
      )
    }

    case 'or-card-carousel': {
      const fields = block.fields as OrCardCarouselProps
      return <OrCardCarousel {...fields} className="!pb-8" />
    }

    case 'or-map': {
      const fields = block.fields as OrMapProps
      return (
        <section>
          <DynamicMapLoader {...fields} />
        </section>
      )
    }

    case 'or-base-content': {
      const fields = block.fields as OrBaseContentProps
      return <OrBaseContent {...fields} nested />
    }

    case 'custom-block': {
      return (block.fields as CustomBlock).content
    }

    default:
      return null
  }
}

export const OrContainerTwoCol = ({
  columnRatio,
  blocks,
  onSubmitForm,
  isFormLoading,
  formErrorNotificationProps,
  formSuccessSnackbarProps,
  gaFormField,
  gaCookie,
  errorElementId,
  gateUnlocked = false,
  onGateUnlock,
  successfulFormId,
}: OrContainerTwoColProps) => {
  const gateFormBlockIndex = useMemo(() => {
    return blocks.findIndex(
      (b) =>
        b.type === 'form' &&
        (b.fields as FormWithTitleAndTypeProps).isGate &&
        (b.fields as FormWithTitleAndTypeProps).gateRevealsContentAfter
    )
  }, [blocks])

  const isBlockHiddenByGate = (index: number) => gateFormBlockIndex >= 0 && index > gateFormBlockIndex && !gateUnlocked

  const { leftBlocks, rightBlocks } = useMemo(() => {
    const left: OrContainerBlock[] = []
    const right: OrContainerBlock[] = []
    blocks.forEach((block, i) => {
      if (i % 2 === 0) left.push(block)
      else right.push(block)
    })
    return { leftBlocks: left, rightBlocks: right }
  }, [blocks])

  const ratioClasses = columnRatioClasses[columnRatio] ?? columnRatioClasses['50-50']

  const sharedProps = {
    onSubmitForm,
    isFormLoading,
    formErrorNotificationProps,
    formSuccessSnackbarProps,
    gaFormField,
    gaCookie,
    errorElementId,
    gateUnlocked,
    onGateUnlock,
    successfulFormId,
  }

  return (
    <article className="lg:mx-auto lg:max-w-screen-lg first-of-type:pt-0 py-8">
      <div className="flex flex-col slg:flex-row gap-8 slg:gap-20 px-4 slg:px-12 lg:px-[10.438rem]">
        <div className={cn('w-full', ratioClasses.left)}>
          {leftBlocks.map((block, index) => {
            const originalIndex = index * 2
            if (isBlockHiddenByGate(originalIndex)) return null
            return (
              <div key={index} className="mb-8 last:mb-0">
                {renderBlock(block, originalIndex, sharedProps)}
              </div>
            )
          })}
        </div>
        <div className={cn('w-full', ratioClasses.right)}>
          {rightBlocks.map((block, index) => {
            const originalIndex = index * 2 + 1
            if (isBlockHiddenByGate(originalIndex)) return null
            return (
              <div key={index} className="mb-8 last:mb-0">
                {renderBlock(block, originalIndex, sharedProps)}
              </div>
            )
          })}
        </div>
      </div>
    </article>
  )
}
